// src/pages/Notifications.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiBell, FiCheck, FiTrash2, FiCheckCircle, FiRefreshCw } from 'react-icons/fi';
import Layout from '../Layout/Layout';
import { useNotifications } from '../contexts/NotificationContext';
import { usePageNotifications } from '../hooks/usePageNotifications';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing[6]} ${({ theme }) => theme.spacing[4]};
`;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const PageSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
`;

const NotificationsContainer = styled.div`
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const NotificationsHeader = styled.div`
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  display: flex;
  justify-content: between;
  align-items: center;
`;

const NotificationsTitle = styled.h2`
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: ${({ variant, theme }) =>
        variant === 'primary' ? theme.colors.primary : 'transparent'};
  color: ${({ variant, theme }) =>
        variant === 'primary' ? theme.colors.background : theme.colors.textSecondary};
  border: 1px solid ${({ variant, theme }) =>
        variant === 'primary' ? theme.colors.primary : theme.colors.divider};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background: ${({ variant, theme }) =>
        variant === 'primary' ? theme.colors.primaryDark : theme.colors.backgroundLighter};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NotificationsList = styled.div`
  max-height: 600px;
  overflow-y: auto;
`;

const NotificationItem = styled.div`
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[4]};
  background: ${({ isRead, theme }) =>
        isRead ? 'transparent' : `${theme.colors.primary}08`};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background: ${({ theme }) => theme.colors.backgroundLighter};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const NotificationIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ type, theme }) => {
        switch (type) {
            case 'achievement': return theme.colors.success;
            case 'course': return theme.colors.primary;
            case 'system': return theme.colors.warning;
            default: return theme.colors.secondary;
        }
    }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.background};
  flex-shrink: 0;
`;

const NotificationContent = styled.div`
  flex: 1;
  
  &:hover {
    opacity: 0.8;
  }
`;

const NotificationTitle = styled.h3`
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[1]} 0;
`;

const NotificationMessage = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
  line-height: 1.5;
`;

const NotificationMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const NotificationTime = styled.span`
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const NotificationBadge = styled.span`
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  background: ${({ type, theme }) => {
        switch (type) {
            case 'achievement': return `${theme.colors.success}20`;
            case 'course': return `${theme.colors.primary}20`;
            case 'system': return `${theme.colors.warning}20`;
            default: return `${theme.colors.secondary}20`;
        }
    }};
  color: ${({ type, theme }) => {
        switch (type) {
            case 'achievement': return theme.colors.success;
            case 'course': return theme.colors.primary;
            case 'system': return theme.colors.warning;
            default: return theme.colors.secondary;
        }
    }};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  font-weight: 500;
  text-transform: capitalize;
`;

const NotificationActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background: ${({ danger, theme }) =>
        danger ? `${theme.colors.error}20` : theme.colors.backgroundLighter};
    color: ${({ danger, theme }) =>
        danger ? theme.colors.error : theme.colors.primary};
    border-color: ${({ danger, theme }) =>
        danger ? theme.colors.error : theme.colors.primary};
  }
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[6]};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  opacity: 0.5;
`;

const LoadingState = styled.div`
  padding: ${({ theme }) => theme.spacing[8]};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ErrorState = styled.div`
  padding: ${({ theme }) => theme.spacing[8]};
  text-align: center;
  color: ${({ theme }) => theme.colors.error};
`;

const Notifications = () => {
    // Only refresh notifications when specifically visiting the notifications page
    const { forceRefreshNotifications } = usePageNotifications(false); // No auto-refresh

    const {
        notifications,
        unreadCount,
        loading,
        error,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearError
    } = useNotifications();

    const [actionLoading, setActionLoading] = useState({});

    useEffect(() => {
        // Only force refresh when user specifically visits the notifications page
        console.log('User visited notifications page, force refreshing...');
        forceRefreshNotifications();
    }, []); // Empty dependency to run only once when component mounts

    // Handle mark as read
    const handleMarkAsRead = async (notificationId) => {
        setActionLoading(prev => ({ ...prev, [`read_${notificationId}`]: true }));
        try {
            console.log('Marking notification as read:', notificationId);
            const success = await markAsRead(notificationId);
            if (!success) {
                console.error('Failed to mark notification as read');
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
        } finally {
            setActionLoading(prev => ({ ...prev, [`read_${notificationId}`]: false }));
        }
    };

    // Handle delete notification
    const handleDeleteNotification = async (notificationId) => {
        setActionLoading(prev => ({ ...prev, [`delete_${notificationId}`]: true }));
        try {
            console.log('Deleting notification:', notificationId);
            const success = await deleteNotification(notificationId);
            if (!success) {
                console.error('Failed to delete notification');
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
        } finally {
            setActionLoading(prev => ({ ...prev, [`delete_${notificationId}`]: false }));
        }
    };

    // Handle mark all as read
    const handleMarkAllAsRead = async () => {
        setActionLoading(prev => ({ ...prev, markAll: true }));
        try {
            console.log('Marking all notifications as read...');
            const success = await markAllAsRead();
            if (!success) {
                console.error('Failed to mark all notifications as read');
            } else {
                console.log('Successfully marked all notifications as read');
            }
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        } finally {
            setActionLoading(prev => ({ ...prev, markAll: false }));
        }
    };

    // Handle notification item click
    const handleNotificationClick = async (notification) => {
        try {
            // Mark as read if not already read
            if (!notification.is_read) {
                console.log('Marking notification as read:', notification.id);
                const success = await markAsRead(notification.id);
                if (!success) {
                    console.error('Failed to mark notification as read');
                    return;
                }
            }

            // Handle navigation if there's a link_url
            if (notification.link_url) {
                window.open(notification.link_url, '_blank');
            }
        } catch (error) {
            console.error('Error handling notification click:', error);
        }
    };

    // Handle refresh
    const handleRefresh = async () => {
        setActionLoading(prev => ({ ...prev, refresh: true }));
        try {
            await fetchNotifications();
        } finally {
            setActionLoading(prev => ({ ...prev, refresh: false }));
        }
    };

    // Format notification time
    const formatTime = (createdAt, timeDiff) => {
        if (timeDiff !== undefined) {
            if (timeDiff < 60) return `${Math.floor(timeDiff)}s ago`;
            if (timeDiff < 3600) return `${Math.floor(timeDiff / 60)}m ago`;
            if (timeDiff < 86400) return `${Math.floor(timeDiff / 3600)}h ago`;
            return `${Math.floor(timeDiff / 86400)}d ago`;
        }

        const date = new Date(createdAt);
        const now = new Date();
        const diff = (now - date) / 1000;

        if (diff < 60) return 'Just now';
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return date.toLocaleDateString();
    };

    return (
        <Layout>
            <PageContainer>
                <PageHeader>
                    <PageTitle>
                        <FiBell />
                        Notifications
                    </PageTitle>
                    <PageSubtitle>
                        Stay updated with your learning progress and important announcements
                        {unreadCount > 0 && ` • ${unreadCount} unread`}
                    </PageSubtitle>
                </PageHeader>

                <NotificationsContainer>
                    <NotificationsHeader>
                        <NotificationsTitle>
                            All Notifications ({notifications.length})
                        </NotificationsTitle>

                        <HeaderActions>
                            <ActionButton
                                onClick={handleRefresh}
                                disabled={actionLoading.refresh}
                                title="Refresh notifications"
                            >
                                <FiRefreshCw />
                                {actionLoading.refresh ? 'Refreshing...' : 'Refresh'}
                            </ActionButton>

                            {unreadCount > 0 && (
                                <ActionButton
                                    variant="primary"
                                    onClick={handleMarkAllAsRead}
                                    disabled={actionLoading.markAll}
                                >
                                    <FiCheckCircle />
                                    {actionLoading.markAll ? 'Marking...' : 'Mark All Read'}
                                </ActionButton>
                            )}
                        </HeaderActions>
                    </NotificationsHeader>

                    <NotificationsList>
                        {loading ? (
                            <LoadingState>
                                <FiRefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
                                <div>Loading notifications...</div>
                            </LoadingState>
                        ) : error ? (
                            <ErrorState>
                                <div>Error loading notifications: {error}</div>
                                <ActionButton onClick={handleRefresh} style={{ marginTop: '16px' }}>
                                    <FiRefreshCw />
                                    Try Again
                                </ActionButton>
                            </ErrorState>
                        ) : notifications.length === 0 ? (
                            <EmptyState>
                                <EmptyStateIcon>
                                    <FiBell />
                                </EmptyStateIcon>
                                <div>No notifications yet</div>
                                <div style={{ fontSize: '14px', marginTop: '8px' }}>
                                    You'll see important updates and announcements here
                                </div>
                            </EmptyState>
                        ) : (
                            notifications.map((notification) => (
                                <NotificationItem
                                    key={notification.id}
                                    isRead={notification.is_read}
                                >
                                    <NotificationIcon type={notification.notification_type}>
                                        <FiBell />
                                    </NotificationIcon>

                                    <NotificationContent
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        {notification.title && (
                                            <NotificationTitle>{notification.title}</NotificationTitle>
                                        )}
                                        <NotificationMessage>{notification.message}</NotificationMessage>

                                        <NotificationMeta>
                                            <NotificationTime>
                                                {formatTime(notification.created_at, notification.time_diff)}
                                            </NotificationTime>

                                            <NotificationBadge type={notification.notification_type}>
                                                {notification.notification_type}
                                            </NotificationBadge>
                                        </NotificationMeta>
                                    </NotificationContent>

                                    <NotificationActions>
                                        {!notification.is_read && (
                                            <IconButton
                                                onClick={() => handleMarkAsRead(notification.id)}
                                                disabled={actionLoading[`read_${notification.id}`]}
                                                title="Mark as read"
                                            >
                                                <FiCheck />
                                            </IconButton>
                                        )}

                                        <IconButton
                                            danger
                                            onClick={() => handleDeleteNotification(notification.id)}
                                            disabled={actionLoading[`delete_${notification.id}`]}
                                            title="Delete notification"
                                        >
                                            <FiTrash2 />
                                        </IconButton>
                                    </NotificationActions>
                                </NotificationItem>
                            ))
                        )}
                    </NotificationsList>
                </NotificationsContainer>
            </PageContainer>
        </Layout>
    );
};

export default Notifications;